import { useEffect, useState } from 'react';
import { api, type CustDupGroup, type CustDupMember, type CustMergeLog } from '../lib/api';
import { ErrorBox } from './DataTable';

/**
 * 거래처 중복 정리(2026-10-17 way 「중복 거래처 하나만 쓰게 · 담당이 다르면 그 담당자 정보는 남기고 · 부서도」).
 *
 * 사업자번호(숫자 10자리)는 같은데 이름이 다른 거래처 묶음을 보여 주고, 사람이 대표를 골라 합친다.
 * 이름까지 같은 묶음은 이미 자동으로 합쳤다(scripts/cust-merge.mjs). 합치면 접수·배차·정산 기록과 담당자가 대표로 옮겨 가고
 * 합친 거래처는 숨겨진다. 「부서」는 그 거래처에서 옮겨 가는 담당자 줄에 붙는다 — 「사랑의교회/청년부」면 청년부.
 * 다른 사업장이면 체크를 풀고 그대로 둔다. 잘못 합쳤으면 아래 「최근 병합」에서 되돌린다.
 */

const SHOW = 30;

/** 이름에서 대표 이름을 뺀 나머지를 부서 후보로 — 「사랑의교회/청년부 김팀장」 → 「청년부 김팀장」. */
function suggestDept(rep: string, name: string): string {
  const clean = (s: string) => s.replace(/^[\s/()\-_·.,]+|[\s/()\-_·.,]+$/g, '').trim();
  const r = rep.trim();
  const n = name.trim();
  if (r && n.startsWith(r) && n !== r) return clean(n.slice(r.length)).slice(0, 100);
  const slash = n.indexOf('/');
  if (slash > 0) return clean(n.slice(slash + 1)).slice(0, 100);
  const paren = n.match(/\(([^)]+)\)/);
  return paren ? clean(paren[1]!).slice(0, 100) : '';
}

/**
 * 대표 기본값 = 이름에 「/담당자」가 없는 곳 → 접수 많은 곳 → 최근 접수 → 번호 작은 곳.
 * 🔴 최근 접수를 먼저 보면 「사랑의교회/안성희간사」(75건)가 「사랑의교회*」(596건)를 제치고 대표가 돼
 *    23곳이 간사 이름 거래처로 합쳐진다(10-17 실화면). 담당자별 거래처는 대표가 아니라 부서로 옮겨 갈 쪽이다.
 */
function pickRep(ms: CustDupMember[]): number {
  const slash = (m: CustDupMember) => (m.cust_name?.includes('/') ? 1 : 0);
  return [...ms].sort((a, b) =>
    slash(a) - slash(b) || b.n_recv - a.n_recv || (b.last_recv ?? '').localeCompare(a.last_recv ?? '') || a.cust_id - b.cust_id)[0]!.cust_id;
}

const bizFmt = (k: string) => `${k.slice(0, 3)}-${k.slice(3, 5)}-${k.slice(5)}`;

export default function CustDupPanel({ canWrite, onClose, onChanged }: {
  canWrite: boolean; onClose: () => void; onChanged: (msg: string) => void;
}) {
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [groups, setGroups] = useState<CustDupGroup[] | null>(null);
  const [recent, setRecent] = useState<CustMergeLog[]>([]);
  const [limit, setLimit] = useState(SHOW);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    setGroups(null);
    setError(null);
    api.listCustDupGroups({ q: q || undefined })
      .then((r) => { if (live) { setGroups(r.groups); setRecent(r.recent); } })
      .catch((e) => { if (live) setError(e instanceof Error ? e.message : String(e)); });
    return () => { live = false; };
  }, [q, nonce]);

  function done(msg: string) {
    setNotice(msg);
    onChanged(msg);
    setNonce((n) => n + 1);
  }

  async function undo(l: CustMergeLog) {
    setError(null);
    try {
      await api.undoCustMerge({ logId: Number(l.id) });
      done(`되돌렸습니다 — 「${l.from_name ?? ''}」(거래처 ${l.from_cust_id})가 다시 보입니다.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const members = groups?.reduce((s, g) => s + g.members.length, 0) ?? 0;

  return (
    <div className="card space-y-4 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-bold">거래처 중복 정리</h2>
        <form className="ml-auto flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); setLimit(SHOW); }}>
          <input className="field w-56" placeholder="거래처명 · 사업자번호" value={term} onChange={(e) => setTerm(e.target.value)} />
          <button className="btn-ghost" type="submit">검색</button>
        </form>
        <button className="btn-ghost" onClick={onClose}>닫기</button>
      </div>
      <p className="text-sm text-ink-soft">
        사업자번호는 같은데 이름이 다른 거래처입니다{groups ? ` — ${groups.length}묶음 · ${members}곳` : ''}.
        같은 사업장이면 <b>대표</b>를 고르고 합치세요. 접수·배차·정산 기록과 거래처담당자·거래처정산담당자가 대표로 옮겨 가고, 합친 거래처는 숨겨집니다(금액은 그대로).
        <b> 부서</b>에 적은 값은 그 거래처에서 옮겨 가는 담당자에 붙습니다. 다른 사업장이면 체크를 풀어 두세요.
      </p>

      {notice && (
        <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          <span>{notice}</span>
          <button className="ml-auto text-xs underline" onClick={() => setNotice(null)}>닫기</button>
        </div>
      )}
      {error && <ErrorBox message={error} />}

      <div className="max-h-[calc(100vh-18rem)] space-y-3 overflow-auto pr-1">
        {!groups && !error && <p className="text-sm text-ink-soft">불러오는 중…</p>}
        {groups?.length === 0 && <p className="text-sm text-ink-soft">정리할 묶음이 없습니다.</p>}
        {groups?.slice(0, limit).map((g) => (
          <GroupCard key={`${g.biz}|${g.members.map((m) => m.cust_id).join(',')}`} g={g} canWrite={canWrite} onMerged={done} onError={setError} />
        ))}
        {groups && groups.length > limit && (
          <button className="btn-ghost w-full" onClick={() => setLimit((n) => n + SHOW)}>더 보기 ({groups.length - limit}묶음 남음)</button>
        )}

        {recent.length > 0 && (
          <div className="rounded-lg border border-line p-3">
            <h3 className="mb-2 text-sm font-bold">최근 병합(이 화면)</h3>
            <table className="w-full text-sm">
              <tbody>
                {recent.map((l) => (
                  <tr key={l.id} className="border-t border-line">
                    <td className="py-1 pr-2 text-ink-soft">{l.created_at.slice(0, 16)}</td>
                    <td className="py-1 pr-2">{l.from_name ?? ''} <span className="text-ink-soft">({l.from_cust_id})</span> → {l.to_name ?? ''} <span className="text-ink-soft">({l.to_cust_id})</span></td>
                    <td className="py-1 pr-2 text-ink-soft">{(l.reason ?? '').replace(/^화면 병합( · )?/, '')}</td>
                    <td className="py-1 pr-2 text-ink-soft">{l.created_by?.split('@')[0]}</td>
                    <td className="py-1 text-right">
                      {l.is_undone
                        ? <span className="text-xs text-ink-soft">되돌림</span>
                        : canWrite && <button className="btn-ghost py-0.5 text-xs" onClick={() => void undo(l)}>되돌리기</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function GroupCard({ g, canWrite, onMerged, onError }: {
  g: CustDupGroup; canWrite: boolean; onMerged: (msg: string) => void; onError: (m: string | null) => void;
}) {
  const [rep, setRep] = useState(() => pickRep(g.members));
  const repName = g.members.find((m) => m.cust_id === rep)?.cust_name ?? '';
  const [checked, setChecked] = useState<Set<number>>(() => new Set(g.members.map((m) => m.cust_id)));
  const [dept, setDept] = useState<Record<number, string>>(() => {
    const r = g.members.find((m) => m.cust_id === pickRep(g.members))?.cust_name ?? '';
    return Object.fromEntries(g.members.map((m) => [m.cust_id, suggestDept(r, m.cust_name ?? '')]));
  });
  const [arm, setArm] = useState(false);
  const [busy, setBusy] = useState(false);

  const from = g.members.filter((m) => m.cust_id !== rep && checked.has(m.cust_id));

  function chooseRep(id: number) {
    setRep(id);
    setArm(false);
    const r = g.members.find((m) => m.cust_id === id)?.cust_name ?? '';
    setDept(Object.fromEntries(g.members.map((m) => [m.cust_id, suggestDept(r, m.cust_name ?? '')])));
  }

  async function merge() {
    if (!arm) { setArm(true); return; }
    setBusy(true);
    onError(null);
    try {
      const r = await api.mergeCusts({ toCustId: rep, from: from.map((m) => ({ custId: m.cust_id, dept: dept[m.cust_id] || undefined })) });
      onMerged(`「${repName}」(거래처 ${rep})로 ${r.merged}곳을 합쳤습니다 — 담당자 ${r.contactsMoved + r.contactsAdded}명 옮김.`);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
      setArm(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-line">
      <div className="flex flex-wrap items-center gap-3 border-b border-line bg-canvas px-3 py-2 text-sm">
        <b>{bizFmt(g.biz)}</b>
        <span className="text-ink-soft">{g.members.length}곳 · 접수 {g.n_recv.toLocaleString()}건</span>
        {canWrite && (
          <button className={`ml-auto ${arm ? 'btn-primary bg-red-600 hover:bg-red-700' : 'btn-primary'}`} disabled={busy || !from.length}
                  onClick={() => void merge()} onBlur={() => setArm(false)}>
            {busy ? '합치는 중…' : arm ? `한 번 더 누르면 ${from.length}곳을 합칩니다` : `${from.length}곳을 대표로 합치기`}
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="text-left text-xs text-ink-soft">
            <tr>
              <th className="px-2 py-1">대표</th>
              <th className="px-2 py-1">합치기</th>
              <th className="px-2 py-1">NO</th>
              <th className="px-2 py-1">거래처명</th>
              <th className="px-2 py-1">거래처담당자</th>
              <th className="px-2 py-1">거래처정산담당자</th>
              <th className="px-2 py-1 text-right">접수</th>
              <th className="px-2 py-1">최근 접수</th>
              <th className="px-2 py-1 text-right">담당자 줄</th>
              <th className="px-2 py-1">부서(옮겨 가는 담당자에)</th>
            </tr>
          </thead>
          <tbody>
            {g.members.map((m) => {
              const isRep = m.cust_id === rep;
              const on = !isRep && checked.has(m.cust_id);
              return (
                <tr key={m.cust_id} className={`border-t border-line ${isRep ? 'bg-brand-50' : on ? '' : 'text-ink-soft'}`}>
                  <td className="px-2 py-1">
                    <input type="radio" name={`rep-${g.biz}`} checked={isRep} disabled={!canWrite} onChange={() => chooseRep(m.cust_id)} aria-label="대표" />
                  </td>
                  <td className="px-2 py-1">
                    {!isRep && (
                      <input type="checkbox" checked={on} disabled={!canWrite} aria-label="합치기"
                             onChange={(e) => { setArm(false); setChecked((cur) => { const n = new Set(cur); if (e.target.checked) n.add(m.cust_id); else n.delete(m.cust_id); return n; }); }} />
                    )}
                  </td>
                  <td className="px-2 py-1 text-ink-soft">{m.cust_id}</td>
                  <td className="px-2 py-1 font-medium">{m.cust_name}{isRep && <span className="ml-2 rounded bg-brand-100 px-1.5 text-xs text-brand-700">대표</span>}</td>
                  <td className="px-2 py-1">{[m.manager, m.manager_phone].filter(Boolean).join(' · ') || '—'}</td>
                  <td className="px-2 py-1">{m.cust_fin_manager || '—'}</td>
                  <td className="px-2 py-1 text-right">{m.n_recv.toLocaleString()}</td>
                  <td className="px-2 py-1">{m.last_recv?.slice(0, 10) ?? '—'}</td>
                  <td className="px-2 py-1 text-right">{m.n_contacts}</td>
                  <td className="px-2 py-1">
                    {on && (
                      <input className="field w-44 py-0.5 text-sm" value={dept[m.cust_id] ?? ''} maxLength={100} placeholder="예: 청년부"
                             onChange={(e) => setDept((cur) => ({ ...cur, [m.cust_id]: e.target.value }))} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
